import { useParams, useSearchParams } from 'react-router-dom';
import { Container, Row } from 'react-bootstrap';
import { useSelector } from 'react-redux';

import Message from '../../components/layout/Message';
import Meta from '../../components/layout/Meta';
import ProductCard from '../../components/product/ProductCard';
import Paginate from '../../components/ui/Paginate';
import ProductCardSkeleton from '../../components/ui/ProductCardSkeleton';
import SortSelect from '../../components/ui/SortSelect';
import { useGetBrandByIdQuery } from '../../slices/brandApiSlice';
import { useGetProductsByBrandIdQuery } from '../../slices/productApiSlice';
import ActiveGlobalDiscountBanner from '../../components/ui/ActiveGlobalDiscountBanner';
import ShippingBehaviorBanner from '../../components/ui/ShippingBehaviorBanner';

const BrandProductsScreen = () => {
    const { userInfo } = useSelector((state) => state.auth);
    const { brandId, pageNumber } = useParams();

    const [searchParams] = useSearchParams();
    const sort = searchParams.get('sort') || '';
    const page = pageNumber || 1;

    const { data, isLoading, isFetching, error } = useGetProductsByBrandIdQuery({
        brandId,
        pageNumber: page,
        priceAssigned: userInfo?.priceAssigned || null,
        sort,
    });

    const { data: brandData } = useGetBrandByIdQuery(brandId, {
        skip: !brandId,
    });

    const products = data?.products || [];
    const normalizedBrand = brandData?.brand || brandData || {};

    const brandName =
        normalizedBrand?.name ||
        data?.brandName ||
        products.find((product) => product?.brand?.name)?.brand?.name ||
        'Marca';

    const brandImage =
        normalizedBrand?.logo ||
        normalizedBrand?.image ||
        data?.brandImage ||
        products.find((product) => product?.brand?.image)?.brand?.image ||
        '/images/default.jpg';

    return (
        <Container className="py-4">
            <Meta title={brandName} />
            <ActiveGlobalDiscountBanner allowGuestPrompt />
            <ShippingBehaviorBanner />

            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4 p-3 p-md-4 border rounded-4 bg-light">
                <div className="d-flex align-items-center gap-3 flex-grow-1">
                    <img
                        src={brandImage}
                        alt={brandName || 'Marca'}
                        onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = '/images/default.jpg';
                        }}
                        style={{ width: '64px', height: '64px', objectFit: 'cover', borderRadius: '12px', flexShrink: 0 }}
                    />
                    <div>
                        <p className="text-muted mb-1 small">Marca mostrada</p>
                        <h1 className="mb-0 section-title">{brandName}</h1>
                    </div>
                </div>
                <div style={{ minWidth: '200px' }}>
                    <SortSelect currentSort={sort} />
                </div>
            </div>

            {isLoading || isFetching ? (
                <Row className="mx-0">
                    <div className="ProductGrid">
                        {[...Array(8)].map((_, idx) => (
                            <ProductCardSkeleton key={idx} />
                        ))}
                    </div>
                </Row>
            ) : error ? (
                <Message variant="danger">
                    {error?.data?.message || error?.error || 'Something went wrong'}
                </Message>
            ) : (
                <>
                    {products.length === 0 ? (
                        <div className="text-center py-5 bg-light rounded-4 border">
                            <p className="text-muted mb-0">No hay productos disponibles para esta marca.</p>
                        </div>
                    ) : (
                        <>
                            <Row className="mx-0">
                                <div className="ProductGrid">
                                    {products.map((product) => (
                                        <ProductCard product={product} key={product._id} />
                                    ))}
                                </div>
                            </Row>
                            <div className="mt-4">
                                <Paginate pages={data.pages} page={data.page} brandId={brandId} sort={sort} />
                            </div>
                        </>
                    )}
                </>
            )}
        </Container>
    );
};

export default BrandProductsScreen;

